// =============================================================================
// lib/siteAddress.ts — fetch + format site address lines for the watermark.
//
// Each HOP / Diary photo upload is project-scoped. Every project has a
// site_id (NOT NULL via FK). Every site has nullable address columns
// (address_line1, address_line2, suburb, state, postcode, country).
//
// We JOIN at upload time and format into TimestampCamera-style lines:
//   line 1: address_line1 (and address_line2 if present, on its own line)
//   line 2: "suburb state postcode" — joined, missing parts omitted
//   line 3: country
//
// T-217 strict rule: missing fields omit their line entirely. No fallback,
// no "Unknown address" placeholder — blank, never wrong.
//
// In-session memo so a batch upload (e.g. 10 photos for one pack) only hits
// the DB once. Memo clears on page reload — fine, sites change rarely.
// =============================================================================
import { supabase } from "@/lib/supabase";

export interface SiteAddressRow {
  address_line1: string | null;
  address_line2: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
}

export function formatSiteAddressLines(s: SiteAddressRow | null | undefined): string[] {
  if (!s) return [];
  const lines: string[] = [];
  if (s.address_line1) lines.push(s.address_line1);
  if (s.address_line2) lines.push(s.address_line2);
  const localityParts = [s.suburb, s.state, s.postcode].filter(Boolean);
  if (localityParts.length) lines.push(localityParts.join(" "));
  if (s.country) lines.push(s.country);
  return lines;
}

const memo = new Map<string, string[]>();

// 10 Oct 2026 — the last good answer is kept on the phone. Before, a failed
// lookup (no signal) was remembered as "no address" for the rest of the
// session, so every photo after it lost its address line; and a photo queued
// offline had nothing to burn in until signal came back.
const LS = "coretel.siteaddr.";
function remember(key: string, lines: string[]) {
  memo.set(key, lines);
  try { localStorage.setItem(LS + key, JSON.stringify(lines)); } catch { /* storage full / private mode */ }
}
function recall(key: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(LS + key) ?? "null");
    return Array.isArray(v) ? v.filter(x => typeof x === "string") : [];
  } catch { return []; }
}

/** For HOP uploads: pack_id → pack's project's site address lines. */
export async function fetchHopPackAddressLines(packId: string): Promise<string[]> {
  const key = `hop:${packId}`;
  const hit = memo.get(key);
  if (hit) return hit;
  // 6 seconds, then the last good answer: on one bar this query does not
  // fail, it hangs — and it sits in front of every photo upload.
  const res: any = await Promise.race([
    supabase
      .from("hop_packs")
      .select("project:projects(site:sites(address_line1, address_line2, suburb, state, postcode, country))")
      .eq("pack_id", packId)
      .maybeSingle()
      .then((r: any) => r, () => null),
    new Promise(resolve => setTimeout(() => resolve(null), 6_000)),
  ]);
  const data = res?.data, error = res?.error;
  if (!res || error || !data) return recall(key);   // not memoised: try again next photo
  const site = (data as any)?.project?.site ?? null;
  const lines = formatSiteAddressLines(site);
  remember(key, lines);
  return lines;
}

/** For Diary uploads: project_id → project's site address lines. */
export async function fetchProjectAddressLines(projectId: string): Promise<string[]> {
  const key = `proj:${projectId}`;
  const hit = memo.get(key);
  if (hit) return hit;
  const { data, error } = await supabase
    .from("projects")
    .select("site:sites(address_line1, address_line2, suburb, state, postcode, country)")
    .eq("project_id", projectId)
    .maybeSingle();
  if (error || !data) return recall(key);   // not memoised: try again next photo
  const site = (data as any)?.site ?? null;
  const lines = formatSiteAddressLines(site);
  remember(key, lines);
  return lines;
}
